import * as arrowDownIcon from '@nordhealth/icons/lib/assets/arrow-down-small.js'
import * as arrowLeftIcon from '@nordhealth/icons/lib/assets/arrow-left-small.js'
import * as arrowRightIcon from '@nordhealth/icons/lib/assets/arrow-right-small.js'
import { html, isServer, LitElement } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { repeat } from 'lit/directives/repeat.js'
import { createKeybindingsHandler } from 'tinykeys'

import { chunk, mapWithOffset } from '../common/collection.js'
import { DirectionController } from '../common/controllers/DirectionController.js'
import { isHorizontalSwipe, SwipeController } from '../common/controllers/SwipeController.js'

import {
  addDays,
  clamp,
  DaysOfWeek,
  endOfMonth,
  endOfWeek,
  getDayNames,
  getMonthNames,
  getViewOfMonth,
  getWeekNumber,
  inRange,
  isEqual,
  isEqualMonth,
  parseISODate,
  printISODate,
  setMonth,
  setYear,
  startOfMonth,
  startOfWeek,
} from '../common/dates.js'
import { observe } from '../common/decorators/observe.js'
import { cond } from '../common/directives/cond.js'
import { range } from '../common/number.js'
import componentStyle from '../common/styles/Component.css'
import Icon from '../icon/Icon.js'
import { LocalizeController } from '../localization/LocalizeController.js'
import style from './Calendar.css'

import { DateRangeSelectEvent } from './DateRangeSelectEvent.js'
import { DateSelectEvent } from './DateSelectEvent.js'
import '../button/Button.js'
import '../visually-hidden/VisuallyHidden.js'

export type DatePredicate = (date: Date) => boolean

Icon.registerIcon(arrowRightIcon)
Icon.registerIcon(arrowLeftIcon)
Icon.registerIcon(arrowDownIcon)

function preventDefault(fn: EventListener) {
  return (e: Event) => {
    e.preventDefault()
    fn(e)
  }
}

const isDateDisabled: DatePredicate = () => false
const isDateHighlighted = () => false

const dialogLabelId = 'dialog-header'

/**
 * Orders two (possibly undefined) dates ascending. Returns `[earlier, later]`,
 * preserving `undefined` slots so callers can render a partial range.
 */
function orderDates(a?: Date, b?: Date): [Date | undefined, Date | undefined] {
  if (a == null || b == null) {
    return [a, b]
  }
  return a.getTime() <= b.getTime() ? [a, b] : [b, a]
}

/**
 * Calendar allows user to pick a date. It comes with built-in
 * functionality that allows you to set a minimum and a maximum allowed date.
 * Please note that the date must be passed in ISO-8601 format.
 *
 * @status ready
 * @category form
 * @fires {DateSelectEvent} change - Dispatched when a date is selected and the value changes.
 * @fires {DateRangeSelectEvent} range-change - Dispatched in `range` mode when a complete range (start and end) has been selected.
 * @fires {DateSelectEvent} nord-focus-date - Dispatched when the calendar's focused date changes.
 *
 * @cssprop [--n-calendar-border-radius=var(--n-border-radius)] - Controls how rounded the corners are, using [border radius tokens](/tokens/#border-radius).
 * @cssprop [--n-calendar-box-shadow=var(--n-box-shadow-popout)] - Controls the surrounding shadow, using [box shadow tokens](/tokens/#box-shadow).
 * @cssprop [--n-calendar-padding-block=var(--n-space-m)] - Controls the block padding of the calendar, using [space tokens](/tokens/#space).
 * @cssprop [--n-calendar-padding-inline=var(--n-space-m)] - Controls the inline padding of the calendar, using [space tokens](/tokens/#space).
 * @cssprop [--n-calendar-day-size=var(--n-space-xl)] - Controls the block and inline size of each day cell, using [space tokens](/tokens/#space).
 * @cssprop [--n-calendar-day-font-size=var(--n-font-size-m)] - Controls the font size of each day cell, using [font size tokens](/tokens/#font-size).
 * @cssprop [--n-calendar-week-number-font-size=var(--n-font-size-s)] - Controls the font size of the week numbers shown by `week-numbers`, using [font size tokens](/tokens/#font-size).
 * @cssprop [--n-calendar-select-label-font-size=var(--n-font-size-xl)] - Controls the font size of the month and year select labels, using [font size tokens](/tokens/#font-size).
 * @cssprop [--n-calendar-nav-padding-inline=11px] - Controls the inline padding of the previous and next month buttons.
 * @cssprop [--n-calendar-nav-gap=var(--n-space-xs)] - Controls the gap between the previous and next month buttons, using [space tokens](/tokens/#space).
 * @cssprop [--n-calendar-header-margin-block-end=var(--n-space-m)] - Controls the block-end margin of the calendar header, using [space tokens](/tokens/#space).
 * @cssprop [--n-calendar-header-padding-block-end=0] - Controls the block-end padding of the calendar header, using [space tokens](/tokens/#space).
 * @cssprop [--n-calendar-header-gap=var(--n-space-s)] - Controls the gap between the month/year selects and the navigation buttons, using [space tokens](/tokens/#space).
 * @cssprop [--n-calendar-inline-size=max-content] - Controls the inline size of the calendar. Defaults to `100%` when the `expand` attribute is set.
 *
 * @localization prevMonthLabel - Accessible label for the previous month button.
 * @localization nextMonthLabel - Accessible label for the next month button.
 * @localization monthSelectLabel - Accessible label for the month select.
 * @localization yearSelectLabel - Accessible label for the year select.
 * @localization weekLabel - Accessible label for the week-number column.
 */
@customElement('nord-calendar')
export default class Calendar extends LitElement {
  static styles = [componentStyle, style]

  @query('.n-calendar-select-month', true) private monthSelectNode!: HTMLElement
  @query(`button[tabindex="0"]`) private focusedDayNode!: HTMLButtonElement

  private direction = new DirectionController(this)
  private swipe = new SwipeController(this, {
    matchesGesture: isHorizontalSwipe,
    onSwipeEnd: ({ distX }) => this.addMonths(distX < 0 ? 1 : -1),
  })

  private shortcuts = createKeybindingsHandler(
    {
      'ArrowRight': preventDefault(() => this.addDays(this.direction.isLTR ? 1 : -1)),
      'ArrowLeft': preventDefault(() => this.addDays(this.direction.isLTR ? -1 : 1)),
      'ArrowDown': preventDefault(() => this.addDays(7)),
      'ArrowUp': preventDefault(() => this.addDays(-7)),
      'Home': preventDefault(() => this.startOfWeek()),
      'End': preventDefault(() => this.endOfWeek()),
      'PageUp': preventDefault(() => this.addMonths(-1)),
      'PageDown': preventDefault(() => this.addMonths(1)),
      'Shift+PageUp': preventDefault(() => this.addYears(-1)),
      'Shift+PageDown': preventDefault(() => this.addYears(1)),
    },
    // Opt out of tinykeys v4's default ignore so holding an arrow key keeps
    // moving the focused day (the default filter drops key-repeat events).
    { ignore: () => false },
  )

  private localize = new LocalizeController<'nord-calendar'>(this, {
    onLangChange: () => this.handleLangChange(),
  })

  /**
   * Whilst dateAdapter is used for handling the formatting/parsing dates in the input,
   * these are used to format dates exclusively for the benefit of screen readers.
   *
   * We prefer DateTimeFormat over date.toLocaleDateString, as the former has
   * better performance when formatting large number of dates. See:
   * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/toLocaleDateString#Performance
   */
  private dateFormatShort!: Intl.DateTimeFormat
  private monthNames!: string[]
  private monthNamesShort!: string[]
  private dayNames!: string[]
  private dayNamesShort!: string[]

  /**
   * The selected date on the calendar. Must be in IS0-8601 format: YYYY-MM-DD.
   */
  @property() value: string = ''

  /**
   * Which day is considered first day of the week? `0` for Sunday, `1` for Monday, etc.
   * Default is Monday.
   */
  @property({ reflect: true, type: Number, attribute: 'first-day-of-week' }) firstDayOfWeek: DaysOfWeek
    = DaysOfWeek.Monday

  /**
   * Minimum date allowed to be picked. Must be in IS0-8601 format: YYYY-MM-DD.
   * This setting can be used alone or together with the max property.
   */
  @property({ reflect: true }) min?: string

  /**
   * Maximum date allowed to be picked. Must be in IS0-8601 format: YYYY-MM-DD.
   * This setting can be used alone or together with the min property.
   */
  @property({ reflect: true }) max?: string

  /**
   * The date that is considered today. Must be in IS0-8601 format: YYYY-MM-DD.
   * If not set, the current local date of the user is used.
   */
  @property({ reflect: true }) today?: string

  /**
   * Controls whether the calendar expands to fill the width of its container.
   */
  @property({ reflect: true, type: Boolean }) expand = false

  /**
   * Controls the size of the calendar.
   */
  @property({ reflect: true }) size: 's' | 'm' = 'm'

  /**
   * Controls which days are disabled and therefore disallowed.
   * For example, this can be used to disallow selection of weekends.
   */
  @property({ attribute: false }) isDateDisabled: DatePredicate = isDateDisabled

  /**
   * Controls which days are highlighted with a small indicator.
   * Returning a "falsy" value will not show an indicator.
   * Returning "truthy" value will show the indicator, but without an accessible label.
   * Returning a string will show the indicator, and use the string as accessible label.
   * It is recommended to return a string rather than a truthy value whenever possible.
   */
  @property({ attribute: false }) isDateHighlighted: (date: Date) => string | boolean = isDateHighlighted

  /**
   * Opt into range selection. When `true`, clicking selects a range start, and
   * the next click selects the end (the two clicks are swapped if the second is
   * earlier than the first). The in-range days are styled, and a hover preview
   * shows the range that would result. A completed range fires `range-change`.
   *
   * This is off by default, so the calendar keeps its single-date behaviour
   * (the `value`/`change` API) unless `range` is set.
   */
  @property({ reflect: true, type: Boolean }) range = false

  /**
   * The start of the selected range, in ISO-8601 format: YYYY-MM-DD. Only used
   * when `range` is set. Pair with `rangeEnd` to control the selection.
   */
  @property({ reflect: true, attribute: 'range-start' }) rangeStart?: string

  /**
   * The end of the selected range, in ISO-8601 format: YYYY-MM-DD. Only used
   * when `range` is set. Pair with `rangeStart` to control the selection.
   */
  @property({ reflect: true, attribute: 'range-end' }) rangeEnd?: string

  /**
   * The number of consecutive months to display side by side, starting from the
   * focused month. Defaults to `1`. Useful with `range` for picking a span that
   * crosses months without paging — the range and its hover preview span every
   * visible month, and the navigation arrows slide the whole window. With more
   * than one month the month/year dropdowns are replaced by static titles.
   */
  @property({ reflect: true, type: Number }) months = 1

  /**
   * Displays the ISO-8601 week number of each visible week in a leading column,
   * headed `#`. Week 1 is the week containing the year's first Thursday.
   * `firstDayOfWeek` moves where a row starts and ends, but the row keeps
   * reporting the ISO week it spans, so the numbers match every other calendar
   * the user reads.
   */
  @property({ reflect: true, type: Boolean, attribute: 'week-numbers' }) weekNumbers = false

  @state() private activeFocus = false
  @state() private focusedDay = new Date()

  /**
   * While picking a range, the first click is held here until the second click
   * completes the range. `undefined` means no in-progress pick.
   */
  @state() private pendingRangeStart?: Date

  /** The day currently hovered while a range pick is in progress (preview). */
  @state() private hoveredDay?: Date

  /**
   * Programmatically move focus to the calendar.
   * @param options An object which controls aspects of the focusing process.
   */
  focus(options?: FocusOptions & { target: 'day' | 'month' }) {
    const target = options?.target ?? 'day'

    if (target === 'day') {
      this.focusedDayNode.focus()
    }
    else if (target === 'month') {
      this.monthSelectNode.focus()
    }
  }

  render() {
    const today = this.getToday()
    const valueAsDate = parseISODate(this.value)

    // Resolve the visible range bounds for `range` mode. While a range pick is
    // in progress, the second bound follows the hovered/focused day so the user
    // sees a live preview of the range that would result.
    const committedStart = parseISODate(this.rangeStart || '')
    const committedEnd = parseISODate(this.rangeEnd || '')
    const previewEnd = this.pendingRangeStart ? (this.hoveredDay ?? this.focusedDay) : undefined
    const [rangeFrom, rangeTo] = this.pendingRangeStart
      ? orderDates(this.pendingRangeStart, previewEnd)
      : orderDates(committedStart, committedEnd)

    const minDate = parseISODate(this.min || '')
    const maxDate = parseISODate(this.max || '')
    const minDateStartOfMonth = minDate ? startOfMonth(minDate) : undefined
    const maxDateEndOfMonth = maxDate ? endOfMonth(maxDate) : undefined

    const currentYear = today.getFullYear()
    const minYear = minDate ? minDate.getFullYear() : currentYear - 100
    const maxYear = maxDate ? maxDate.getFullYear() : currentYear + 10

    if (isServer) {
      // Needed when run on a server, because the `onLangChange` callback does not get triggered, so we need to manually
      // call `handleLangChange` to set the localized values for month and day names, which the `render` method relies on.
      this.handleLangChange()
    }

    // Render `months` consecutive month grids, anchored on the focused month.
    // They all read the same range state, so a range (and its hover preview) spans
    // months automatically.
    const count = Math.max(1, this.months)
    const months = Array.from(
      { length: count },
      (_, i) => new Date(this.focusedDay.getFullYear(), this.focusedDay.getMonth() + i, 1),
    )
    const multi = count > 1

    // In multi-month mode adjacent months share the same overlap days (the
    // trailing days of one month are the leading days of the next), so rendering
    // the out-of-month days in every grid would emit the same date twice —
    // duplicating `tabindex="0"`, `aria-pressed` and range styling. Disable
    // boundary navigation when ANY visible month already reaches the bound, so
    // the arrows never appear enabled-but-noop.
    const reachesMin = minDate != null && months.some(monthDate => isEqualMonth(minDate, monthDate))
    const reachesMax = maxDate != null && months.some(monthDate => isEqualMonth(maxDate, monthDate))

    const dayCell = (day: Date, monthDate: Date) => {
      const isInMonth = isEqualMonth(day, monthDate)

      // In multi-month mode the leading/trailing overlap days belong to the
      // adjacent grid, so each date is rendered (and owned) by exactly one
      // month. Render an inert placeholder here instead of a duplicate button.
      if (multi && !isInMonth) {
        return html`<td class="n-calendar-cell" aria-hidden="true"></td>`
      }

      const outsideRange = !inRange(day, minDate, maxDate)
      const isToday = isEqual(day, today)
      const isDisabled = this.isDateDisabled(day)
      const isHighlighted = this.isDateHighlighted(day)
      const formattedDate = this.dateFormatShort.format(day)

      // In range mode the "selected" days are the two range ends; otherwise it is
      // the single `value`.
      const isRangeStart = this.range && rangeFrom != null && isEqual(day, rangeFrom)
      const isRangeEnd = this.range && rangeTo != null && isEqual(day, rangeTo)
      const isInRange = this.range && rangeFrom != null && rangeTo != null && day >= rangeFrom && day <= rangeTo
      const isSelected = this.range ? isRangeStart || isRangeEnd : isEqual(day, valueAsDate)
      const sameDayRange = rangeFrom != null && isEqual(rangeFrom, rangeTo)

      const accessibleLabel
        = isHighlighted && typeof isHighlighted === 'string' ? `${formattedDate}, ${isHighlighted}` : formattedDate

      return html`
        <td class="n-calendar-cell">
          <button
            type="button"
            tabindex=${isEqual(day, this.focusedDay) ? 0 : -1}
            class=${classMap({
              'n-calendar-day': true,
              'is-outside': outsideRange,
              'is-month': isInMonth,
              'is-highlighted': isHighlighted,
              'is-in-range': isInRange && !isSelected,
              'is-range-start': isRangeStart && !sameDayRange,
              'is-range-end': isRangeEnd && !sameDayRange,
            })}
            @click=${() => this.handleDaySelect(day)}
            @mouseenter=${() => this.handleDayHover(day)}
            @keydown=${this.shortcuts}
            ?disabled=${outsideRange}
            aria-disabled=${cond(isDisabled, 'true')}
            aria-pressed=${isSelected ? 'true' : 'false'}
            aria-current=${cond(isToday, 'date')}
            aria-label=${accessibleLabel}
          >
            <span aria-hidden="true">${day.getDate()}</span>
          </button>
        </td>
      `
    }

    // The rich month/year selects only make sense for a single month. With more
    // than one month, each shows a static title with the prev/next arrows on the
    // first/last month, so navigation slides the whole window of months.
    const header = (monthDate: Date, index: number, labelId: string) => {
      const monthIndex = monthDate.getMonth()
      const monthYear = monthDate.getFullYear()
      if (multi) {
        const isFirst = index === 0
        const isLast = index === count - 1
        return html`
          <div class="n-calendar-header is-static">
            <nord-button
              class=${classMap({ 'n-calendar-prev': true, 'is-hidden': !isFirst })}
              size=${this.size}
              square
              variant="plain"
              @click=${this.handlePreviousMonthClick}
              ?disabled=${!isFirst || reachesMin}
              type="button"
            >
              <nord-visually-hidden>${this.localize.term('prevMonthLabel')}</nord-visually-hidden>
              <nord-icon name=${this.direction.isLTR ? 'arrow-left-small' : 'arrow-right-small'} size=${this.size === 's' ? 'xs' : 's'}></nord-icon>
            </nord-button>

            <h2 class="n-calendar-title" id=${labelId} aria-live="polite" aria-atomic="true">
              ${this.monthNames[monthIndex]} ${monthYear}
            </h2>

            <nord-button
              class=${classMap({ 'n-calendar-next': true, 'is-hidden': !isLast })}
              size=${this.size}
              square
              variant="plain"
              @click=${this.handleNextMonthClick}
              ?disabled=${!isLast || reachesMax}
              type="button"
            >
              <nord-visually-hidden>${this.localize.term('nextMonthLabel')}</nord-visually-hidden>
              <nord-icon name=${this.direction.isLTR ? 'arrow-right-small' : 'arrow-left-small'} size=${this.size === 's' ? 'xs' : 's'}></nord-icon>
            </nord-button>
          </div>
        `
      }
      return html`
        <div class="n-calendar-header">
          <div>
            <nord-visually-hidden>
              <h2 id=${labelId} aria-live="polite" aria-atomic="true">${this.monthNames[monthIndex]}, ${monthYear}</h2>
            </nord-visually-hidden>

            <div class="n-calendar-select">
              <select aria-label=${this.localize.term('monthSelectLabel')} class="n-calendar-select-month" @input=${this.handleMonthSelect}>
                ${this.monthNames.map(
                  (month, i) => html`
                    <option
                      value=${i}
                      ?selected=${i === monthIndex}
                      ?disabled=${!inRange(new Date(monthYear, i, 1), minDateStartOfMonth, maxDateEndOfMonth)}
                    >
                      ${month}
                    </option>
                  `,
                )}
              </select>
              <div class="n-calendar-select-label" aria-hidden="true">
                <span>${this.monthNamesShort[monthIndex]}</span>
                <nord-icon color="var(--n-color-icon)" name="arrow-down-small" size="xxs"></nord-icon>
              </div>
            </div>

            <div class="n-calendar-select">
              <select aria-label=${this.localize.term('yearSelectLabel')} class="n-calendar-select-year" @input=${this.handleYearSelect}>
                ${repeat(
                  range(minYear, maxYear),
                  year => year,
                  year => html`<option ?selected=${year === monthYear}>${year}</option>`,
                )}
              </select>
              <div class="n-calendar-select-label" aria-hidden="true">
                <span>${monthYear}</span>
                <nord-icon color="var(--n-color-icon)" name="arrow-down-small" size="xxs"></nord-icon>
              </div>
            </div>
          </div>

          <div class="n-calendar-nav">
            <nord-button
              class="n-calendar-prev"
              size=${this.size}
              square
              @click=${this.handlePreviousMonthClick}
              ?disabled=${isEqualMonth(minDate, monthDate)}
              type="button"
            >
              <nord-visually-hidden>${this.localize.term('prevMonthLabel')}</nord-visually-hidden>
              <nord-icon name=${this.direction.isLTR ? 'arrow-left-small' : 'arrow-right-small'} size=${this.size === 's' ? 'xs' : 's'}></nord-icon>
            </nord-button>

            <nord-button
              class="n-calendar-next"
              size=${this.size}
              square
              @click=${this.handleNextMonthClick}
              ?disabled=${isEqualMonth(maxDate, monthDate)}
              type="button"
            >
              <nord-visually-hidden>${this.localize.term('nextMonthLabel')}</nord-visually-hidden>
              <nord-icon name=${this.direction.isLTR ? 'arrow-right-small' : 'arrow-left-small'} size=${this.size === 's' ? 'xs' : 's'}></nord-icon>
            </nord-button>
          </div>
        </div>
      `
    }

    return html`
      <div class=${classMap({ 'n-calendar': true, 'has-multiple-months': multi })}>
        ${months.map((monthDate, index) => {
          const labelId = `${dialogLabelId}-${index}`
          return html`
            <div class="n-calendar-month">
              ${header(monthDate, index, labelId)}

              <table
                class="n-calendar-table"
                aria-labelledby=${labelId}
                @focusin=${this.enableActiveFocus}
                @focusout=${this.disableActiveFocus}
              >
                <thead>
                  <tr>
                    ${cond(
                      this.weekNumbers,
                      html`
                        <th class="n-calendar-week-header" scope="col">
                          <span aria-hidden="true">#</span>
                          <nord-visually-hidden>${this.localize.term('weekLabel')}</nord-visually-hidden>
                        </th>
                      `,
                    )}
                    ${mapWithOffset(
                      this.dayNames,
                      this.firstDayOfWeek,
                      (dayName, i) => html`
                        <th class="n-calendar-table-header" scope="col">
                          <span aria-hidden="true">${this.dayNamesShort[i]}</span>
                          <nord-visually-hidden>${dayName}</nord-visually-hidden>
                        </th>
                      `,
                    )}
                  </tr>
                </thead>
                <tbody>
                  ${chunk(getViewOfMonth(monthDate, this.firstDayOfWeek), 7).map(
                    week => html`
                      <tr class="n-calendar-row">
                        ${cond(
                          this.weekNumbers,
                          html`<th class="n-calendar-week-number" scope="row">${getWeekNumber(week[0], this.firstDayOfWeek)}</th>`,
                        )}
                        ${week.map(day => dayCell(day, monthDate))}
                      </tr>
                    `,
                  )}
                </tbody>
              </table>
            </div>
          `
        })}
      </div>
    `
  }

  @observe('value')
  protected handleValueChange() {
    if (this.range) {
      return
    }
    this.setFocusedDay(parseISODate(this.value) || this.getToday())
  }

  @observe('rangeStart')
  @observe('rangeEnd')
  protected handleRangeChange() {
    if (!this.range) {
      return
    }
    // Focus the most relevant end of the committed range so the calendar opens
    // showing it, falling back to today when nothing is set.
    const start = parseISODate(this.rangeStart || '')
    const end = parseISODate(this.rangeEnd || '')
    this.setFocusedDay(start || end || this.getToday())
  }

  @observe('focusedDay', 'updated')
  protected handleFocusedDayChange() {
    if (this.activeFocus) {
      this.focusedDayNode.focus()
    }
  }

  private handleLangChange() {
    const lang = this.localize.resolvedLang
    this.dateFormatShort = new Intl.DateTimeFormat(lang, { day: 'numeric', month: 'long' })
    this.monthNames = getMonthNames(lang, 'long')
    this.monthNamesShort = getMonthNames(lang, 'short')
    this.dayNames = getDayNames(lang, 'long')
    this.dayNamesShort = getDayNames(lang, 'narrow')
  }

  private handleDaySelect = (day: Date) => {
    const isWithinBounds = inRange(day, parseISODate(this.min || ''), parseISODate(this.max || ''))
    const isAllowed = !this.isDateDisabled(day)

    if (!isWithinBounds || !isAllowed) {
      return
    }

    if (this.range) {
      this.selectRangeDay(day)
      return
    }

    this.value = printISODate(day)
    this.dispatchEvent(new DateSelectEvent('change', day))
  }

  /**
   * Two-click range selection: the first click starts a pending range, the
   * second completes it. The two ends are swapped if the second click is before
   * the first, so the committed range is always ordered `start <= end`.
   */
  private selectRangeDay(day: Date) {
    if (!this.pendingRangeStart) {
      this.pendingRangeStart = day
      this.hoveredDay = day
      this.rangeStart = printISODate(day)
      this.rangeEnd = printISODate(day)
      return
    }

    const [start, end] = orderDates(this.pendingRangeStart, day) as [Date, Date]
    this.pendingRangeStart = undefined
    this.hoveredDay = undefined
    this.rangeStart = printISODate(start)
    this.rangeEnd = printISODate(end)
    this.dispatchEvent(new DateRangeSelectEvent('range-change', start, end))
  }

  private handleDayHover = (day: Date) => {
    if (this.range && this.pendingRangeStart) {
      this.hoveredDay = day
    }
  }

  private getToday() {
    return parseISODate(this.today || '') || new Date()
  }

  private addDays(days: number) {
    this.setFocusedDay(addDays(this.focusedDay, days))
  }

  private addMonths(months: number) {
    this.setMonth(this.focusedDay.getMonth() + months)
  }

  private addYears(years: number) {
    this.setYear(this.focusedDay.getFullYear() + years)
  }

  private startOfWeek() {
    this.setFocusedDay(startOfWeek(this.focusedDay, this.firstDayOfWeek))
  }

  private endOfWeek() {
    this.setFocusedDay(endOfWeek(this.focusedDay, this.firstDayOfWeek))
  }

  private setMonth(month: number) {
    const min = setMonth(startOfMonth(this.focusedDay), month)
    const max = endOfMonth(min)
    const date = setMonth(this.focusedDay, month)

    this.setFocusedDay(clamp(date, min, max))
  }

  private setYear(year: number) {
    const min = setYear(startOfMonth(this.focusedDay), year)
    const max = endOfMonth(min)
    const date = setYear(this.focusedDay, year)

    this.setFocusedDay(clamp(date, min, max))
  }

  private setFocusedDay(day: Date) {
    this.focusedDay = clamp(day, parseISODate(this.min || ''), parseISODate(this.max || ''))
    this.dispatchEvent(new DateSelectEvent('nord-focus-date', this.focusedDay))
  }

  private handleMonthSelect = (e: Event) => {
    this.setMonth(Number.parseInt((e.target as HTMLSelectElement).value, 10))
  }

  private handleYearSelect = (e: Event) => {
    this.setYear(Number.parseInt((e.target as HTMLSelectElement).value, 10))
  }

  private handleNextMonthClick = (event: MouseEvent) => {
    event.preventDefault()
    this.addMonths(1)
  }

  private handlePreviousMonthClick = (event: MouseEvent) => {
    event.preventDefault()
    this.addMonths(-1)
  }

  private enableActiveFocus = () => {
    this.activeFocus = true
  }

  private disableActiveFocus = (e: FocusEvent) => {
    const table = e.currentTarget as Node
    const relatedTarget = e.relatedTarget as Node

    if (relatedTarget && !table.contains(relatedTarget)) {
      this.activeFocus = false
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-calendar': Calendar
  }
}
